/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import type { OrganizationDetails } from '@sportninja/common/services/organization/readOrganization'
import { paragraphSmall } from 'src/components/css'
import { RosterProvider } from 'src/pages/Team/UnifiedRoster/context/RosterContext'
import { useSelectedTeamRoster } from '../../hooks/useSelectedTeamRoster'
import {
  LOADING_ROSTER_CAPABILITIES,
  type RosterCapabilities,
} from '../../hooks/useRosterPermissions'
import PanelSkeleton from '../panel/PanelSkeleton'
import RosterSection, {
  type RosterCounts,
  type RosterTab,
} from '../roster/RosterSection'
import ForceRosterSelection from './ForceRosterSelection'
import { shouldStopReportingRosterCapabilities } from './shouldStopReportingRosterCapabilities'

const translate = t as (key: string, options?: unknown) => string

interface TeamRosterTabProps {
  teamId: string
  /** Forwarded to the Add Player search modal's title. */
  teamName: string
  divisionScheduleId: string | null
  sportId: string | null
  canEdit: boolean
  /** Owning organization, forwarded to the roster's Add permission gates. */
  organization: OrganizationDetails | null
  /** Bumped by the panel header's Add Player button — see `RosterSection`. */
  addPlayerSignal?: number
  /** Bumped after a CSV import lands, to refetch the roster from outside the provider. */
  refreshKey?: number
  /** Reports roster counts up for the panel's stat strip. */
  onCountsChange?: (counts: RosterCounts) => void
  /** Reports the visible roster tab up, so the Add button can follow it. */
  onTabChange?: (tab: RosterTab) => void
  /** Reports whether the Add buttons are permitted, for the panel header. */
  onCapabilitiesChange?: (capabilities: RosterCapabilities) => void
  /**
   * The full-screen shell's layer while the tab is full screen, else absent.
   * Forwarded to the roster modals so they stack above the shell.
   */
  hostZIndex?: number
}

/**
 * The selected team's roster.
 *
 * Mounts `RosterProvider` — which owns the players / team-officials fetch and
 * the `refresh()` every mutating flow calls — but renders the Operations
 * roster grid rather than `UnifiedRosterPage`, so this panel follows the tab's
 * own grid language instead of the Team screen's tables. The provider stays
 * because the shared player and official modals read from it.
 */
const TeamRosterTab = ({
  teamId,
  teamName,
  divisionScheduleId,
  sportId,
  canEdit,
  organization,
  addPlayerSignal,
  refreshKey,
  onCountsChange,
  onTabChange,
  onCapabilitiesChange,
  hostZIndex,
}: TeamRosterTabProps) => {
  'use memo'

  const { roster, isLoading, isError } = useSelectedTeamRoster({
    teamId,
    divisionScheduleId,
    enabled: true,
  })

  // `OperationsTab` resets capabilities to the LOADING state on every
  // selection change (isLoading: true), on the assumption this component is
  // about to report in. The error and no-roster branches below never mount
  // `RosterSection` — nothing will ever report — so without this they would
  // leave the header's Add button stuck on a skeleton forever. Only fires for
  // those two terminal, no-report states; the loading branch leaves the
  // inherited isLoading: true alone, and the roster branch below hands off to
  // RosterSection's own report.
  const stopsReporting = shouldStopReportingRosterCapabilities({
    isLoading,
    isError,
    roster,
  })
  useEffect(() => {
    if (stopsReporting) {
      // Everything closed, but NOT loading: this is the settled "no roster or
      // failed fetch" answer, and a stuck skeleton would be worse than nothing.
      onCapabilitiesChange?.({
        ...LOADING_ROSTER_CAPABILITIES,
        isLoading: false,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopsReporting])

  if (isLoading) {
    return <PanelSkeleton />
  }

  if (isError) {
    return <p css={messageStyle}>{translate('errors:somethingWentWrong')}</p>
  }

  if (!roster) {
    return <p css={messageStyle}>{translate('Web:noRosterForDivision')}</p>
  }

  return (
    <RosterProvider
      // Remount per resolved roster: `useRosterSelect` captures the initial
      // roster on FIRST RENDER only, so a live instance re-pointed at another
      // team/division would keep the previous roster's players.
      key={`${teamId}-${roster.id}`}
      teamId={teamId}
      existingRosterIdFromLocationState={roster.id}
      refreshKey={refreshKey}
      onComplete={() => undefined}
    >
      {/* The prop above is honoured only on first render, and only when no
          `sn_schedule` param is in the URL — this pins the selection either way. */}
      <ForceRosterSelection rosterId={roster.id} />
      <RosterSection
        teamId={teamId}
        teamName={teamName}
        sportId={sportId}
        canEdit={canEdit}
        organization={organization}
        addPlayerSignal={addPlayerSignal}
        onCountsChange={onCountsChange}
        onTabChange={onTabChange}
        onCapabilitiesChange={onCapabilitiesChange}
        hostZIndex={hostZIndex}
      />
    </RosterProvider>
  )
}

const messageStyle = css`
  ${paragraphSmall}
  color: ${colors.NEUTRAL_100};
  text-align: center;
  padding: 24px;
  margin: 0;
`

export default TeamRosterTab
